export const authClientIPHeaderNames = [
  "X-Yumesaku-Client-IP",
  "X-Yumesaku-Client-Timestamp",
  "X-Yumesaku-Client-Signature",
] as const;

const protectedPaths = new Set([
  "/api/auth/v1/signInWithEmail",
  "/api/auth/sign-in/email",
  "/api/mobile/auth/login",
  "/api/auth/email-otp/send-verification-otp",
  "/api/auth/v1/forgotPassword",
]);

export function isAuthRateLimitedRequest(method: string, path: string): boolean {
  if (method !== "POST") return false;
  // Go は URL を 1 回復号した Path で経路を選ぶ。同じ規則で判定しないと、
  // %66orgotPassword のような符号化形が署名なしで通常の経路へ届く。
  let decoded: string;
  try {
    decoded = decodeURIComponent(path);
  } catch {
    return false;
  }
  return protectedPaths.has(decoded);
}

export async function signedAuthClientIPHeaders({
  request,
  secret,
  method,
  path,
}: {
  request: Request;
  secret: string | undefined;
  method: string;
  path: string;
}): Promise<Headers> {
  const headers = new Headers();
  if (!isAuthRateLimitedRequest(method, path)) return headers;
  const ip = request.headers.get("CF-Connecting-IP")?.trim();
  if (!ip) return headers;
  if (!secret) {
    // 画面だけの staging (VITE_YUMESAKU_STAGING=1 のビルド) は経路の合言葉を持たない。印を付けずに送り、api-go は送り元の住所で数える
    if ((import.meta as { env?: Record<string, string | undefined> }).env?.VITE_YUMESAKU_STAGING === "1") return headers;
    throw new Error("Auth client IP signing is not configured");
  }
  const timestamp = String(Math.floor(Date.now() / 1000));
  const encoder = new TextEncoder();
  const key = await crypto.subtle.importKey(
    "raw",
    encoder.encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const signature = await crypto.subtle.sign(
    "HMAC",
    key,
    encoder.encode(`auth-client-ip-v1\n${method}\n${path}\n${timestamp}\n${ip}`),
  );
  headers.set(authClientIPHeaderNames[0], ip);
  headers.set(authClientIPHeaderNames[1], timestamp);
  headers.set(
    authClientIPHeaderNames[2],
    Array.from(new Uint8Array(signature), (byte) =>
      byte.toString(16).padStart(2, "0"),
    ).join(""),
  );
  return headers;
}

export async function authProxyHeaders(
  request: Request,
  secret: string | undefined,
): Promise<Headers> {
  const headers = new Headers(request.headers);
  headers.delete("X-Yumesaku-Internal");
  for (const name of authClientIPHeaderNames) headers.delete(name);
  const signed = await signedAuthClientIPHeaders({
    request,
    secret,
    method: request.method,
    path: new URL(request.url).pathname,
  });
  signed.forEach((value, name) => headers.set(name, value));
  return headers;
}
